/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { AddCircle, BorderColor, Clear, Delete, Edit, Search } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Grid, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridRowId, GridRowModel, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import { toast } from "react-toastify";

import AutoComplete from "@/components/hookForms/autoComplete";
import { Container } from "@/theme/globalStyles";
import DataTable from "@/components/dataTable";
import { useAuth } from "@/context/global";
import IGenericResult from "@/interfaces/IGenericResult";
import serviceConcessionaria from "@/services/serviceConcessionaria";
import DialogNew from "./modal/dialogAdd";
import DialogEdit from "./modal/dialogEdit";
import DialogEditFormula from "./modal/dialogEditFormula";
import serviceModeloFatura from "@/services/serviceModeloFatura";
import messages from "@/utils/messages";
import ConfirmationDialog from "@/components/confirmationDialog";
import TextInput from "@/components/hookForms/textField";
import useThrowAsyncError from "@/hooks/useThrowAsyncError";

const tipoCampoText = {
    F: "Fatura",
    C: "Calculado",
    H: "Histórico",
    R: "Referência",
};

const CadastroPosicoes = () => {
    const { setTitlePage } = useAuth();
    const [concessionariaDataOptions, setConcessionariaDataOptions] = useState<IGenericResult[]>([]);
    const [modeloDataOptions, setModeloDataOptions] = useState<any>([]);
    const [descricaoFaturaValoresDataOptions, setDescricaoFaturaValoresDataOptions] = useState([]);
    const [openModalAdd, setOpenModalAdd] = useState<boolean>(false);
    const [exclusionLoading, setExclusionLoading] = useState(false);
    const [exclusionState, setExclusionState] = useState({ data: "", open: false });
    const [data, setData] = useState<any>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState(null);
    const [page, setPage] = useState(0);
    const [rowCount, setRowCount] = useState(10);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "",
            sort: "" as GridSortDirection,
        },
    ]);

    useEffect(() => {
        setTitlePage("Captura de Faturas - Cadastro das Posições");
    }, [setTitlePage]);

    const [pageSize, setPageSize] = useState(100);
    const [editFormulaState, setEditFormulaState] = useState({
        open: false,
        faturaPosicaoId: 0,
        formula: "",
    });

    const [editState, setEditState] = useState({
        open: false,
        faturaPosicaoId: 0,
        modeloFaturaId: 0,
        descricaoFaturaValoresId: 0,
        concessionariaID: 0,
        descricaoModelo: "",
        campoFatura: "",
        posicao: "",
        referencia: "",
        ocorrencia: "",
        tipoCampo: "",
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        defaultValues: {
            concessionariaID: "",
            modeloFaturaID: "",
            descricaoChave: "",
        },
    });

    const actionColumns = useMemo(
        () => [
            {
                headerName: "Operações",
                field: "operations",
                type: "actions",
                width: 220,
                hide: false,
                getActions: (params: { id: GridRowId; row: GridRowModel }) => [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Editar Fórmula">
                                <BorderColor />
                            </Tooltip>
                        }
                        label="EditarFormula"
                        onClick={() => {
                            setEditFormulaState({
                                open: true,
                                faturaPosicaoId: params.row.faturaPosicaoId,
                                formula: params.row.formula,
                            });
                        }}
                        disabled={params.row.tipoCampo !== "C"}
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Editar">
                                <Edit />
                            </Tooltip>
                        }
                        label="Editar"
                        onClick={() =>
                            setEditState({
                                open: true,
                                descricaoFaturaValoresId: params.row.descricaoFaturaValoresId,
                                faturaPosicaoId: params.row.faturaPosicaoId,
                                modeloFaturaId: params.row.modeloFaturaId,
                                concessionariaID: params.row.concessionariaID,
                                descricaoModelo: params.row.descricaoModelo,
                                campoFatura: params.row.campoFatura,
                                posicao: params.row.posicao,
                                referencia: params.row.referencia,
                                ocorrencia: params.row.ocorrencia,
                                tipoCampo: params.row.tipoCampo,
                            })
                        }
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Excluir">
                                <Delete />
                            </Tooltip>
                        }
                        label="Excluir"
                        onClick={() => setExclusionState({ data: params.id.toString(), open: true })}
                    />,
                ],
            },
        ],
        []
    );

    const columns = useMemo(
        () => [
            {
                headerName: "ID",
                field: "faturaPosicaoId",
                type: "number",
                headerAlign: "left",
                identity: true,
                maxWidth: 110,
                flex: 1,
                id: 70,
            },
            {
                headerName: "Concessionária",
                field: "nomeConcessionaria",
                type: "string",
                minWidth: 300,
                flex: 1,
            },
            {
                headerName: "Descrição Modelo",
                field: "descricaoModelo",
                type: "string",
                minWidth: 350,
                flex: 1,
            },
            {
                headerName: "Campo da Fatura",
                field: "descricaoChave",
                type: "string",
                minWidth: 300,
                flex: 1,
            },
            {
                headerName: "Posição",
                headerAlign: "center",
                field: "posicao",
                type: "number",
                align: "center",
                minWidth: 100,
                flex: 1,
            },
            {
                headerName: "Referência",
                field: "referencia",
                type: "string",
                minWidth: 300,
                flex: 1,
            },
            {
                headerName: "Ocorrência",
                field: "ocorrencia",
                headerAlign: "center",
                type: "number",
                align: "center",
                minWidth: 120,
                flex: 1,
            },
            {
                headerName: "Tipo do Campo",
                field: "tipoCampo",
                type: "string",
                minWidth: 150,
                flex: 1,
                valueFormatter: (item: any) => {
                    return tipoCampoText[item.value];
                },
            },
            {
                headerName: "Fórmula de Cálculo",
                field: "formula",
                type: "string",
                minWidth: 250,
                flex: 1,
            },
            ...actionColumns,
        ],
        [actionColumns]
    );

    useEffect(() => {
        getAllWithFilters(filters);
    }, [page, pageSize, sortModel]);

    useEffect(() => {
        setConcessionariaData();
        setModeloData();
        setDescricaoFaturaValoresData();
    }, []);

    const setModeloData = async () => {
        serviceModeloFatura
            .getAllModelos()
            .then((result) => {
                setModeloDataOptions(result?.data);
            })
            .catch(() => {
                setModeloDataOptions([]);
            });
    };

    const setDescricaoFaturaValoresData = async () => {
        serviceModeloFatura
            .getAllCampos()
            .then((result) => {
                setDescricaoFaturaValoresDataOptions(
                    result?.data?.map((data) => {
                        return { value: data.descricaoFaturaValoresID, label: data.descricaoChave };
                    })
                );
            })
            .catch(() => {
                setDescricaoFaturaValoresDataOptions([]);
            });
    };

    const throwAsyncError = useThrowAsyncError();

    const getAllWithFilters = async (filters: any) => {
        setData([]);
        setIsLoading(true);

        serviceModeloFatura
            .getPosicoesWithFilter({
                ...filters,
                take: pageSize,
                currentPage: page + 1,
                sortExpression: sortModel[0]?.field ?? "",
                sortDirection: sortModel[0]?.sort ?? "",
            })
            .then((result) => {
                if (!result) {
                    throw new Error("Falha ao carregar os dados.");
                }
                setData(result.data.content);
                setRowCount(result.data.numberOfElements);
            })
            .catch((err) => {
                setData([]);
                setRowCount(0);
                throwAsyncError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleDelete = async () => {
        setExclusionLoading(true);

        serviceModeloFatura
            .deletePosicao(exclusionState.data)
            .then((result) => {
                if (result) {
                    toast.success(messages.deleteSucess);
                }
            })
            .catch((err) => {
                toast.error(messages.deleteError, err);
            })
            .finally(() => {
                setExclusionLoading(false);
                setExclusionState({ data: "", open: false });
                getAllWithFilters(filters);
            });
    };

    const setConcessionariaData = async () => {
        serviceConcessionaria
            .getConcessionarias()
            .then((result) => {
                setConcessionariaDataOptions(result?.data);
            })
            .catch(() => {
                setConcessionariaDataOptions([]);
            });
    };

    const onClear = () => {
        reset({ concessionariaID: "" });
        setFilters(null);
        getAllWithFilters(null);
    };

    const onSuccessAction = () => {
        getAllWithFilters(filters);
        setConcessionariaData();
        setModeloData();
        setDescricaoFaturaValoresData();
    };

    const onSubmit = (values: any) => {
        const newFilters = {
            ...values,
        };

        setFilters(newFilters);
        getAllWithFilters(newFilters);
    };

    return (
        <Container>
            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2} mt={2}>
                                <Grid item md={12} xs={12}>
                                    <Grid container spacing={2} display="flex" alignItems="center">
                                        <Grid item md={2} xs={12}>
                                            <AutoComplete
                                                id="concessionariaID"
                                                name="concessionariaID"
                                                label="Concessionária"
                                                options={concessionariaDataOptions ?? []}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <AutoComplete
                                                id="modeloFaturaID"
                                                name="modeloFaturaID"
                                                label="Descrição Modelo"
                                                options={modeloDataOptions?.map((data) => {
                                                    return { value: data.modeloFaturaId, label: data.descricaoModelo };
                                                })}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextInput
                                                id="descricaoChave"
                                                name="descricaoChave"
                                                label="Campo da Fatura"
                                                control={control}
                                                error={errors}
                                                maxLength={20}
                                            />
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <Button
                                                id="pesquisar"
                                                disableElevation
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Search />}
                                                type="submit"
                                            >
                                                Pesquisar
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <Button
                                                id="limpar"
                                                style={{ marginTop: 16, marginBottom: 8 }}
                                                variant="outlined"
                                                color="warning"
                                                startIcon={<Clear />}
                                                onClick={() => onClear()}
                                                disableElevation
                                                fullWidth
                                                size="medium"
                                            >
                                                Limpar
                                            </Button>
                                        </Grid>
                                        <Grid item md={2} xs={12}>
                                            <Button
                                                id="novo"
                                                variant="contained"
                                                color="primary"
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<AddCircle />}
                                                onClick={() => setOpenModalAdd(true)}
                                                disableElevation
                                                fullWidth
                                                size="medium"
                                            >
                                                Novo
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>
                    <Box sx={{ height: 450 }}>
                        <DataTable
                            columns={columns}
                            rows={data ?? []}
                            rowId={(row: GridRowModel) => row.faturaPosicaoId}
                            rowCount={rowCount}
                            isLoading={isLoading}
                            onPageChange={(currentPage: number) => setPage(currentPage)}
                            onPageSizeChange={(currentPageSize: number) => setPageSize(currentPageSize)}
                            pageSize={pageSize}
                            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
                            disableRowSelectionOnClick={true}
                        />
                    </Box>
                </CardContent>
            </Card>
            <DialogNew
                open={openModalAdd}
                modeloDataOptions={modeloDataOptions}
                descricaoFaturaValoresDataOptions={descricaoFaturaValoresDataOptions}
                onSuccess={() => onSuccessAction()}
                onClose={() => setOpenModalAdd(false)}
            />

            <DialogEdit
                open={editState.open}
                modeloDataOptions={modeloDataOptions}
                descricaoFaturaValoresDataOptions={descricaoFaturaValoresDataOptions}
                onSuccess={() => onSuccessAction()}
                data={{
                    descricaoFaturaValoresId: editState.descricaoFaturaValoresId,
                    faturaPosicaoId: editState.faturaPosicaoId,
                    modeloFaturaId: editState.modeloFaturaId,
                    concessionariaID: editState.concessionariaID,
                    descricaoModelo: editState.descricaoModelo,
                    campoFatura: editState.campoFatura,
                    posicao: editState.posicao,
                    referencia: editState.referencia,
                    ocorrencia: editState.ocorrencia,
                    tipoCampo: editState.tipoCampo,
                }}
                onClose={() =>
                    setEditState({
                        open: false,
                        descricaoFaturaValoresId: 0,
                        modeloFaturaId: 0,
                        faturaPosicaoId: 0,
                        concessionariaID: 0,
                        descricaoModelo: "",
                        campoFatura: "",
                        posicao: "",
                        referencia: "",
                        ocorrencia: "",
                        tipoCampo: "",
                    })
                }
            />

            <DialogEditFormula
                open={editFormulaState.open}
                onSuccess={onSuccessAction}
                data={editFormulaState}
                descricaoFaturaValoresDataOptions={descricaoFaturaValoresDataOptions}
                onClose={() =>
                    setEditFormulaState({
                        open: false,
                        faturaPosicaoId: 0,
                        formula: "",
                    })
                }
            />

            <ConfirmationDialog
                open={exclusionState?.open}
                loading={exclusionLoading}
                title={"Excluir Posição"}
                message={messages.deleteConfirmation}
                cancelButtonText={"Não"}
                confirmButtonText={"Sim"}
                onClose={() => setExclusionState({ data: "", open: false })}
                onCancel={() => setExclusionState({ data: "", open: false })}
                onConfirm={() => {
                    handleDelete();
                }}
            />
        </Container>
    );
};

export default CadastroPosicoes;
